import { CheckIcon } from "@chakra-ui/icons";
import {
  Box,
  Flex,
  Button,
  Text,
  Tooltip,
  useBreakpointValue,
  Input,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  VStack,
  HStack,
  Badge,
  Card,
  CardBody,
  Divider,
  Icon,
} from "@chakra-ui/react";
import { AppContext } from "AppProvider/AppContext";
import React, { useContext, useEffect, useState, useMemo } from "react";
import { appRequest } from "routes";
import { FiPackage, FiFileText, FiSettings } from "react-icons/fi";

type ModalProps = {
  onClose: () => void;
};

function WorkflowListComponent({ onClose }: ModalProps) {
  const {
    userWorkflows,
    activeWorkflow,
    accountDetails,
    toastNotify,
    setActiveWorkflow,
    getDefaultWorkFlow,
    workflowProcess,
    setDefaultAbsent,
  } = useContext(AppContext);

  const [isHovered, setIsHovered] = useState<any>(null);
  const [defaultLoading, setDefaultLoading] = useState<any>("");
  const [active, setActive] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const isTruncatedValue = useBreakpointValue({ base: true, lg: false });

  const handleMouseEnter = (index: number) => setIsHovered(index);
  const handleMouseLeave = () => setIsHovered(null);

  console.log("the workflow process are", workflowProcess);

  const changeWorkFlowStatus = async (id: any, status: Boolean) => {
    try {
      setDefaultLoading(id);
      const response: any = await appRequest("workflow", "updateStatus", {
        id,
        status,
      });

      if (response.status === "success") {
        toastNotify({
          title: `Status updated successfully`,
          message: response?.message,
          type: "success",
        });
        getDefaultWorkFlow();
        sessionStorage.removeItem("activeWorkflowId");
        setActiveWorkflow(null);
        setDefaultAbsent(false);
        onClose();
      } else {
        toastNotify({
          title: `Failed to Update Status`,
          message: response?.message || "Something went wrong.",
          type: "error",
        });
      }
    } catch (err: any) {
      toastNotify({
        title: `Failed to Update Status`,
        message: err?.message || "Something went wrong.",
        type: "error",
      });
    } finally {
      setDefaultLoading(null);
    }
  };

  const activeWorkflowId = sessionStorage.getItem("activeWorkflowId");
  useEffect(() => {
    if (activeWorkflowId) {
      setActiveWorkflow(activeWorkflowId);
    }
  }, [activeWorkflowId, setActiveWorkflow]);

  useEffect(() => {
    if (active === true) {
      getDefaultWorkFlow();
      onClose();
    }
    setActive(false);
  }, [activeWorkflowId]);

  // ✅ Group workflows by processType -> processName
  const groupedWorkflows: Record<
    string,
    Record<string, any[]>
  > = useMemo(() => {
    const processGroups: Record<string, Record<string, any[]>> = {
      Po: {
        Emerging: [],
        Domestic: [],
        Tillomed: [],
        Marcan: [],
        zuventus: [],
        mmt: [],
        Others: [],
        foreign: [],
        CRM: [],
        pr:[],
        sr:[]
      },
      NonPo: {
        Emerging: [],
        Domestic: [],
        Tillomed: [],
        Marcan: [],
        zuventus: [],
        mmt: [],
        foreign: [],
        Others: [],
        CRM: [],
        pr:[],
        sr:[]
      },
      Others: {
        Emerging: [],
        Domestic: [],
        Tillomed: [],
        Marcan: [],
        zuventus: [],
        mmt: [],
        foreign: [],
        Others: [],
        pr: [],
        sr:[],
        CRM: []
      },
    };

    userWorkflows.forEach((wf: any) => {
      const sessionProcess = sessionStorage.getItem("process");
      const pname = wf?.processName?.toLowerCase() || "";

      // If session is foreign, only allow foreign workflows
      if (sessionProcess === "foreign" && !pname.includes("foreign")) {
        return;
      }

      const type = wf?.processType
        ? wf.processType.toLowerCase() === "po"
          ? "Po"
          : wf?.processType?.toLowerCase() === "nonpo"
            ? "NonPo"
            : "Others"
        : "Others";

      if (pname.includes("emerging")) processGroups[type].Emerging.push(wf);
      else if (pname.includes("crm")) processGroups[type].CRM.push(wf);
      else if (pname.includes("domestic"))
        processGroups[type].Domestic.push(wf);
      else if (pname.includes("zuventus"))
        processGroups[type].zuventus.push(wf);
      else if (pname.includes("mmt")) processGroups[type].mmt.push(wf);
      else if (pname.includes("tillomed"))
        processGroups[type].Tillomed.push(wf);
      else if (pname.includes("pr"))
        processGroups[type].pr.push(wf);
      else if (pname.includes("sr"))
        processGroups[type].sr.push(wf);
      else if (pname.includes("marcan")) processGroups[type].Marcan.push(wf);
      else if (pname.includes("foreign")) processGroups[type].foreign.push(wf);
      else processGroups[type].Others.push(wf);
    });

    return processGroups;
  }, [userWorkflows]);

  // ✅ Always keep PO + NonPO, conditionally show Others, filter strictly for foreign
  const cleanedGroups = useMemo(() => {
    const result: Record<string, Record<string, any[]>> = {};
    const sessionProcess = sessionStorage.getItem("process");

    const typesToIterate = sessionProcess === "foreign" ? ["NonPo"] : ["Po", "NonPo"];

    typesToIterate.forEach((ptype) => {
      result[ptype] = {};
      Object.entries(groupedWorkflows[ptype]).forEach(([pname, list]) => {
        // If session is foreign, only allow foreign sub-group
        if (sessionProcess === "foreign" && pname !== "foreign") return;

        if (list.length > 0) {
          result[ptype][pname] = list;
        }
      });
    });

    if (sessionProcess !== "foreign") {
      // Only include Others if it has workflows and not in foreign mode
      const otherGroups: Record<string, any[]> = {};
      Object.entries(groupedWorkflows.Others).forEach(([pname, list]) => {
        if (list.length > 0) {
          otherGroups[pname] = list;
        }
      });
      if (Object.keys(otherGroups).length > 0) {
        result["Others"] = otherGroups;
      }
    }

    return result;
  }, [groupedWorkflows]);

  // ✅ Apply search
  const filterList = (list: any[]) =>
    list.filter((workflow: any) =>
      workflow?.workFlowName
        ?.toLowerCase()
        .includes(searchTerm.trim().toLowerCase())
    );

  const renderWorkflowItem = (workflow: any, index: number) => (
    <Card
      key={workflow._id}
      variant="outline"
      shadow={activeWorkflow === workflow._id ? "lg" : "sm"}
      borderColor={activeWorkflow === workflow._id ? "teal.400" : "gray.200"}
      bg={activeWorkflow === workflow._id ? "teal.50" : "white"}
      cursor="pointer"
      transition="all 0.2s"
      _hover={{
        shadow: "md",
        transform: "translateY(-3px)",
        borderColor: "teal.300",
      }}
      // onClick={() => {
      //   setActive(true);
      //   sessionStorage.setItem("activeWorkflowId", workflow._id);
      // }}
      onMouseEnter={() => handleMouseEnter(index)}
      onMouseLeave={handleMouseLeave}
    >
      <CardBody py={3} px={4}>
        <HStack justify="space-between" align="center">
          <Box w={{ base: "180px", md: "100%", lg: "100%" }} overflow="hidden">
            {isTruncatedValue ? (
              <Tooltip
                label={workflow?.workFlowName}
                textAlign="center"
                hasArrow
              >
                <Text fontWeight="semibold" whiteSpace="nowrap">
                  {workflow?.workFlowName}
                </Text>
              </Tooltip>
            ) : (
              <Tooltip label={workflow?.workFlowName}>
                <Text fontWeight="semibold" whiteSpace="nowrap">
                  {workflow?.flowName || workflow?.workFlowName}
                </Text>
              </Tooltip>
            )}
          </Box>

          {accountDetails?.defaultWorkflow !== workflow._id ? (
            <Tooltip hasArrow label="Set as Default">
              <Button
                size="xs"
                rounded="full"
                variant="solid"
                colorScheme="teal"
                onClick={(e) => {
                  e.stopPropagation();
                  changeWorkFlowStatus(workflow._id, !workflow?.defaultActive);
                }}
                isLoading={defaultLoading === workflow._id}
              >
                <CheckIcon />
              </Button>
            </Tooltip>
          ) : (
            <Badge
              colorScheme="teal"
              fontSize="0.75rem"
              variant="solid"
              rounded="full"
              px={3}
            >
              Default
            </Badge>
          )}
        </HStack>
      </CardBody>
    </Card>
  );

  const processIcons: Record<string, any> = {
    PO: FiPackage,
    NonPO: FiFileText,
    Others: FiSettings,
  };

  return (
    <Box>
      {/* ✅ Sticky Search Box */}
      <Box position="sticky" top="0" zIndex="10" bg="white" pb={2}>
        <Input
          placeholder="🔍 Search workflows..."
          mb={2}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          rounded="full"
          shadow="sm"
          focusBorderColor="teal.400"
        />
        <Divider />
      </Box>

      {/* ✅ Outer Tabs (ProcessType) */}
      <Tabs variant="soft-rounded" colorScheme="teal" isFitted mt={3}>
        <TabList bg="gray.50" rounded="xl" p={1} shadow="sm">
          {Object.keys(cleanedGroups).map((ptype) => (
            <Tab
              key={ptype}
              fontSize="sm"
              fontWeight="600"
              _selected={{ bg: "teal.100", shadow: "sm" }}
              rounded="lg"
              py={2}
            >
              <HStack spacing={2}>
                <Icon as={processIcons[ptype]} />
                <Text>{ptype === "NonPo" ? "Non-PO" : ptype}</Text>
              </HStack>
            </Tab>
          ))}
        </TabList>

        <TabPanels>
          {Object.entries(cleanedGroups).map(([ptype, subGroups]) => (
            <TabPanel
              key={ptype}
              px={2}
              py={4}
              bg="gray.50"
              rounded="md"
              shadow="inner"
            >
              {/* ✅ Inner Tabs (ProcessName categories) */}
              {Object.keys(subGroups).length > 0 ? (
                <Tabs variant="enclosed" colorScheme="teal" isFitted size="lg">
                  <TabList
                    bg="gray.50"
                    rounded="xl"
                    p={1}
                    shadow="sm"
                    overflowX="auto"
                    whiteSpace="nowrap"
                  >
                    {Object.keys(subGroups).map((pname) => (
                      <Tab
                        _selected={{ bg: "teal.100", shadow: "sm" }}
                        key={pname}
                        px={4}
                        fontWeight="600"
                      >
                        {pname}
                        <Badge ml={2} colorScheme="teal" rounded="full">
                          {filterList(subGroups[pname]).length}
                        </Badge>
                      </Tab>
                    ))}
                  </TabList>
                  <TabPanels>
                    {Object.entries(subGroups).map(([pname, wfs]) => (
                      <TabPanel key={pname} px={1}>
                        <VStack
                          spacing={3}
                          align="stretch"
                          maxH="320px"
                          overflowY="auto"
                        >
                          {filterList(wfs).length > 0 ? (
                            filterList(wfs).map(renderWorkflowItem)
                          ) : (
                            <Flex
                              justify="center"
                              align="center"
                              py={12}
                              direction="column"
                            >
                              <Icon
                                as={FiSettings}
                                boxSize={8}
                                color="gray.400"
                                mb={2}
                              />
                              <Text color="gray.500" fontSize="sm">
                                No workflows found.
                              </Text>
                            </Flex>
                          )}
                        </VStack>
                      </TabPanel>
                    ))}
                  </TabPanels>
                </Tabs>
              ) : (
                <Flex
                  justify="center"
                  align="center"
                  py={12}
                  direction="column"
                >
                  <Icon as={FiSettings} boxSize={8} color="gray.400" mb={2} />
                  <Text color="gray.500" fontSize="sm">
                    No workflows found.
                  </Text>
                </Flex>
              )}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </Box>
  );
}

export default WorkflowListComponent;